import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { container } from '@/di';
import { TYPES } from '@/di/tokens';
import { IPanelPagoRepository } from '@/domain/repositories/IPanelPagoRepository';
import { PanelPago } from '@/domain/entities/PanelPago';
import type { TenantGridDTO } from '@/domain/repositories/ITenantRepository';
import { useToast } from '@/presentation/contexts/ToastContext';
import { useAuth } from '@/presentation/contexts/AuthContext';
import { Button, Input } from '@/presentation/components/common';
import { usePlanCatalog } from '@/presentation/hooks/usePlanCatalog';
import { ViewPlanDetailModal } from '@/presentation/components/billing/ViewPlanDetailModal';
import { RegisterPaymentModal } from '@/presentation/components/tenants/RegisterPaymentModal';
import { ClinicInvoiceModal } from '@/presentation/components/billing/ClinicInvoiceModal';
import {
  ContactDebtorModal,
  type ContactTarget,
} from '@/presentation/components/collections/ContactDebtorModal';
import {
  CONTACT_STATUS_COLORS,
  fetchLatestContacts,
  type LastContactMap,
} from '@/presentation/components/collections/contactHistoryUtils';
import type { PlanCatalogItem } from '@/domain/types/planCatalog';

type SortField =
  | 'nombreClinica'
  | 'monto'
  | 'estadoPagoDescripcion'
  | 'fechaPago'
  | 'medioPagoDescripcion'
  | 'planId';
type SortDirection = 'asc' | 'desc';

function paymentToTenant(payment: PanelPago): TenantGridDTO {
  const guid =
    payment.tenantId ||
    (payment.clinicaId.isGuid() ? String(payment.clinicaId.getValue()) : null);
  const numericId =
    typeof payment.clinicaId.getValue() === 'number'
      ? (payment.clinicaId.getValue() as number)
      : null;
  const planIdNum = payment.planId ? Number(payment.planId) : undefined;

  return {
    id: guid || numericId || 0,
    nombre: payment.nombreClinica,
    emailAdministrador: payment.emailAdministrador || '',
    telefonoContacto: payment.telefonoContacto,
    activo: true,
    fechaActivoUTC: new Date().toISOString(),
    montoPeriodoPago: payment.monto,
    planAdminId: planIdNum != null && Number.isFinite(planIdNum) ? planIdNum : undefined,
    estadoPagoId: payment.estadoPagoId,
    estadoPagoDescripcion: payment.estadoPagoDescripcion,
    fechaPeriodoPago: payment.fechaPeriodoPago,
  };
}

interface PeriodPaymentsTableProps {
  periodo: string;
  embedded?: boolean;
  onTotalsChange?: (totals: { cobrado: number; pendiente: number; cuentas: number }) => void;
}

const money = (n: number) =>
  `$${n.toLocaleString('es-AR', { maximumFractionDigits: 0 })}`;

export const PeriodPaymentsTable: React.FC<PeriodPaymentsTableProps> = ({
  periodo,
  embedded = false,
  onTotalsChange,
}) => {
  const { t } = useTranslation();
  const { canAdminister, canOperateBilling } = useAuth();
  const { error: showError } = useToast();
  const { plans } = usePlanCatalog();
  const [payments, setPayments] = useState<PanelPago[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'paid' | 'pending'>('all');
  const [sortField, setSortField] = useState<SortField>('nombreClinica');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [selectedPlan, setSelectedPlan] = useState<PlanCatalogItem | null>(null);
  const [contactTarget, setContactTarget] = useState<ContactTarget | null>(null);
  const [paymentTenant, setPaymentTenant] = useState<TenantGridDTO | null>(null);
  const [invoiceTarget, setInvoiceTarget] = useState<{ tenantId: string; nombre: string } | null>(null);
  const [lastContacts, setLastContacts] = useState<LastContactMap>({});
  const [filtersOpen, setFiltersOpen] = useState(false);

  const showActions = canAdminister || canOperateBilling;

  const openContact = (payment: PanelPago) => {
    const tenantId =
      payment.tenantId ||
      (payment.clinicaId.isGuid() ? String(payment.clinicaId.getValue()) : null);
    const clinicaId =
      typeof payment.clinicaId.getValue() === 'number'
        ? (payment.clinicaId.getValue() as number)
        : null;
    setContactTarget({
      tenantId,
      clinicaId,
      nombreClinica: payment.nombreClinica,
      nombreContacto: payment.nombreDueno,
      planName: resolvePlan(payment.planId)?.nombre ?? null,
      emailAdministrador: payment.emailAdministrador,
      telefonoContacto: payment.telefonoContacto,
      periodo: payment.fechaPeriodoPago || periodo,
      totalAdeudado: Math.max(payment.monto, payment.deudaAcumulada || 0),
    });
  };

  const openRegisterPayment = (payment: PanelPago) => {
    const tenant = paymentToTenant(payment);
    if (!tenant.id || tenant.id === 0) {
      showError(t('pages.collections.missingTenant'));
      return;
    }
    setPaymentTenant(tenant);
  };

  const openInvoice = (payment: PanelPago) => {
    const tenantId =
      payment.tenantId ||
      (payment.clinicaId.isGuid() ? String(payment.clinicaId.getValue()) : null);
    if (!tenantId) {
      showError(t('pages.collections.missingTenant'));
      return;
    }
    setInvoiceTarget({ tenantId, nombre: payment.nombreClinica });
  };

  const planById = useMemo(() => {
    const map = new Map<number, PlanCatalogItem>();
    for (const plan of plans) map.set(plan.id, plan);
    return map;
  }, [plans]);

  const resolvePlan = (planId: string | null): PlanCatalogItem | null => {
    if (!planId) return null;
    const id = Number(planId);
    if (!Number.isFinite(id)) return null;
    return planById.get(id) ?? null;
  };

  const planLabel = (planId: string | null): string => {
    const plan = resolvePlan(planId);
    if (plan) return plan.nombre;
    if (planId) return `${t('pages.billing.plan')} #${planId}`;
    return '—';
  };

  const loadPayments = useCallback(async () => {
    setIsLoading(true);
    try {
      const repository = container.get<IPanelPagoRepository>(TYPES.IPanelPagoRepository);
      const periodPayments = await repository.getByPeriodo(periodo);
      setPayments(periodPayments);
    } catch (err) {
      showError(err instanceof Error ? err.message : t('pages.billing.noPayments'));
    } finally {
      setIsLoading(false);
    }
  }, [periodo, showError, t]);

  useEffect(() => {
    void loadPayments();
  }, [loadPayments]);

  useEffect(() => {
    const ids = payments
      .map((p) => p.tenantId || (p.clinicaId.isGuid() ? String(p.clinicaId.getValue()) : null))
      .filter((id): id is string => Boolean(id));
    void fetchLatestContacts(ids).then(setLastContacts);
  }, [payments]);

  useEffect(() => {
    if (!onTotalsChange) return;
    const isPaid = (p: PanelPago) => {
      const e = (p.estadoPagoDescripcion || '').trim().toLowerCase();
      if (e === 'abonado' || e === 'abonado con mora' || e === 'bonificado' || e === 'gratis') {
        return true;
      }
      return Boolean(p.fechaPago);
    };
    const cobrado = payments.filter(isPaid).reduce((s, p) => s + p.monto, 0);
    const pendiente = payments.filter((p) => !isPaid(p)).reduce((s, p) => s + p.monto, 0);
    onTotalsChange({ cobrado, pendiente, cuentas: payments.length });
  }, [payments, onTotalsChange]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortIcon = (field: SortField) => {
    if (sortField !== field) return ' ↕';
    return sortDirection === 'asc' ? ' ↑' : ' ↓';
  };

  const filtered = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const rows = payments.filter((payment) => {
      if (statusFilter === 'paid' && !payment.fechaPago) return false;
      if (statusFilter === 'pending' && payment.fechaPago) return false;
      if (!term) return true;
      return (
        payment.nombreClinica.toLowerCase().includes(term) ||
        payment.estadoPagoDescripcion.toLowerCase().includes(term) ||
        (payment.notas && payment.notas.toLowerCase().includes(term)) ||
        payment.monto.toString().includes(term) ||
        (payment.medioPagoDescripcion || '').toLowerCase().includes(term) ||
        planLabel(payment.planId).toLowerCase().includes(term)
      );
    });

    const dir = sortDirection === 'asc' ? 1 : -1;
    rows.sort((a, b) => {
      let cmp = 0;
      switch (sortField) {
        case 'nombreClinica':
          cmp = a.nombreClinica.localeCompare(b.nombreClinica, 'es');
          break;
        case 'monto':
          cmp = a.monto - b.monto;
          break;
        case 'estadoPagoDescripcion':
          cmp = a.estadoPagoDescripcion.localeCompare(b.estadoPagoDescripcion, 'es');
          break;
        case 'fechaPago': {
          const ta = a.fechaPago ? new Date(a.fechaPago).getTime() : 0;
          const tb = b.fechaPago ? new Date(b.fechaPago).getTime() : 0;
          cmp = ta - tb;
          break;
        }
        case 'medioPagoDescripcion':
          cmp = (a.medioPagoDescripcion || '').localeCompare(b.medioPagoDescripcion || '', 'es');
          break;
        case 'planId':
          cmp = planLabel(a.planId).localeCompare(planLabel(b.planId), 'es');
          break;
      }
      return cmp * dir;
    });

    return rows;
  }, [payments, searchTerm, statusFilter, sortField, sortDirection, planById, t]);

  const getStatusBadgeClass = (estado: string) => {
    if (estado === 'Abonado') return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
    if (estado === 'Mora') return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
    if (estado === 'Impago') return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
    if (estado === 'Gratis') return 'bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-200';
    return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
  };

  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
  };

  const getStatusFilterLabel = (value: typeof statusFilter): string => {
    if (value === 'paid') return t('pages.billing.filterPaid');
    if (value === 'pending') return t('pages.billing.filterPendingPeriod');
    return t('pages.billing.all');
  };

  const activeFilterChips = [
    ...(searchTerm.trim()
      ? [{
          key: 'search',
          label: `${t('common.search')}: ${searchTerm.trim()}`,
          onRemove: () => setSearchTerm(''),
        }]
      : []),
    ...(statusFilter !== 'all'
      ? [{
          key: 'status',
          label: `${t('pages.billing.status')}: ${getStatusFilterLabel(statusFilter)}`,
          onRemove: () => setStatusFilter('all'),
        }]
      : []),
  ];

  const thClass =
    'px-3 py-2 text-xs font-medium text-gray-500 uppercase bg-slate-50 dark:bg-gray-900 cursor-pointer hover:bg-slate-100 dark:hover:bg-gray-800 select-none';

  const contactChip = (payment: PanelPago) => {
    const tid =
      payment.tenantId ||
      (payment.clinicaId.isGuid() ? String(payment.clinicaId.getValue()) : null);
    const last = tid ? lastContacts[tid] : null;
    if (!last) return null;
    return (
      <span
        className={`panel-chip ${
          CONTACT_STATUS_COLORS[last.deliveryStatus] || CONTACT_STATUS_COLORS.pending
        }`}
        title={`${t('pages.collections.contact.lastContact')}: ${last.channel} · ${last.recipient}`}
      >
        {t(`pages.collections.contact.status.${last.deliveryStatus}`, {
          defaultValue: last.deliveryStatus,
        })}
        {' · '}
        {last.channel === 'email' ? 'Email' : 'WA'}
      </span>
    );
  };

  const openPlan = (planId: string | null) => {
    const plan = resolvePlan(planId);
    if (plan) setSelectedPlan(plan);
    else showError(t('pages.billing.planUnknown'));
  };

  return (
    <div
      className={
        embedded
          ? 'overflow-hidden'
          : 'bg-white dark:bg-gray-800 rounded-xl border border-slate-200 dark:border-gray-700 shadow-sm overflow-hidden'
      }
    >
      <div className="space-y-2 border-b border-slate-200 p-2 dark:border-gray-700">
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setFiltersOpen((v) => !v)}
            className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition ${
              filtersOpen || activeFilterChips.length > 0
                ? 'border-teal-500/50 bg-teal-50 text-teal-800 dark:border-teal-400/40 dark:bg-teal-900/30 dark:text-teal-200'
                : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-gray-700 dark:bg-gray-800 dark:text-slate-200 dark:hover:bg-gray-700'
            }`}
            aria-expanded={filtersOpen}
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75} aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 4h18M6 10h12M9 16h6" />
            </svg>
            {filtersOpen ? t('common.hideFilters') : t('common.showFilters')}
            {activeFilterChips.length > 0 && (
              <span className="inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-teal-600 px-1.5 py-0.5 text-[11px] font-semibold leading-none text-white">
                {activeFilterChips.length}
              </span>
            )}
            <svg
              className={`h-3.5 w-3.5 transition-transform ${filtersOpen ? 'rotate-180' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
              aria-hidden
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {!filtersOpen && activeFilterChips.length > 0 && (
            <div className="flex min-w-0 flex-1 flex-wrap gap-1.5">
              {activeFilterChips.map((chip) => (
                <button
                  key={chip.key}
                  type="button"
                  onClick={chip.onRemove}
                  className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-teal-500/50 bg-teal-50 px-2 py-0.5 text-[11px] font-medium leading-4 text-teal-700 transition-colors hover:bg-teal-100 dark:border-teal-400/50 dark:bg-teal-900/20 dark:text-teal-200 dark:hover:bg-teal-900/40"
                  title={chip.label}
                >
                  <span className="truncate">{chip.label}</span>
                  <span className="text-sm leading-none" aria-hidden>×</span>
                </button>
              ))}
            </div>
          )}

          <div className="ml-auto flex items-center gap-2">
            <p className="hidden text-xs tabular-nums text-slate-500 dark:text-slate-400 md:block">
              {filtered.length}
              {' / '}
              {payments.length}
            </p>
            <Button variant="secondary" size="sm" onClick={() => void loadPayments()} disabled={isLoading}>
              {t('common.refresh')}
            </Button>
          </div>
        </div>

        {filtersOpen && (
          <div className="rounded-lg border border-slate-200 bg-white p-2 shadow-sm dark:border-gray-700 dark:bg-gray-800">
            <div className="flex flex-col gap-2 lg:flex-row lg:items-center">
              <div className="grid flex-1 grid-cols-1 gap-2 md:grid-cols-[minmax(18rem,1fr)_minmax(10rem,0.35fr)]">
                <Input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder={t('pages.billing.searchPayments')}
                  className="h-8 py-1 text-xs"
                  aria-label={t('common.search')}
                  title={t('common.search')}
                />
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value as 'all' | 'paid' | 'pending')}
                  className="block h-8 w-full rounded-md border border-gray-300 bg-white px-2 text-xs text-gray-900 focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  aria-label={t('pages.billing.status')}
                  title={t('pages.billing.status')}
                >
                  <option value="all">{t('pages.billing.all')}</option>
                  <option value="paid">{t('pages.billing.filterPaid')}</option>
                  <option value="pending">{t('pages.billing.filterPendingPeriod')}</option>
                </select>
              </div>

              <button
                type="button"
                onClick={clearFilters}
                disabled={activeFilterChips.length === 0}
                className="inline-flex h-8 w-full items-center justify-center rounded-md border border-slate-300 bg-white text-slate-500 transition-colors hover:bg-slate-50 hover:text-slate-800 disabled:cursor-not-allowed disabled:opacity-40 dark:border-gray-600 dark:bg-gray-700 dark:text-slate-300 dark:hover:bg-gray-600 dark:hover:text-white lg:w-8"
                title={t('common.clear')}
                aria-label={t('common.clear')}
              >
                <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75} aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3m-8 0h10" />
                </svg>
                <span className="ml-2 text-sm lg:hidden">{t('common.clear')}</span>
              </button>
            </div>

            {activeFilterChips.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {activeFilterChips.map((chip) => (
                  <button
                    key={chip.key}
                    type="button"
                    onClick={chip.onRemove}
                    className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-teal-500/50 bg-teal-50 px-2 py-0.5 text-[11px] font-medium leading-4 text-teal-700 transition-colors hover:bg-teal-100 dark:border-teal-400/50 dark:bg-teal-900/20 dark:text-teal-200 dark:hover:bg-teal-900/40"
                    title={chip.label}
                  >
                    <span className="truncate">{chip.label}</span>
                    <span className="text-sm leading-none" aria-hidden>×</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {isLoading ? (
        <div className="px-4 py-10 text-center text-sm text-slate-500">{t('common.loading')}</div>
      ) : filtered.length === 0 ? (
        <div className="px-4 py-10 text-center text-sm text-slate-500">{t('pages.billing.noPayments')}</div>
      ) : (
        <>
          {/* Cards mobile */}
          <div className="max-h-[55vh] space-y-2 overflow-y-auto p-2 md:hidden">
            {filtered.map((payment) => {
              const initial = (payment.nombreClinica.trim().charAt(0) || '?').toUpperCase();
              return (
                <div key={payment.id} className="panel-list-card px-3.5 py-3">
                  <div className="flex items-start gap-3">
                    <span className="mt-0.5 inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-slate-100 text-sm font-bold text-slate-700 dark:bg-gray-700 dark:text-slate-200">
                      {initial}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p
                            className="truncate text-[15px] font-semibold leading-snug text-slate-900 dark:text-white"
                            title={payment.nombreClinica}
                          >
                            {payment.nombreClinica}
                          </p>
                          <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                            {planLabel(payment.planId)}
                            {payment.medioPagoDescripcion
                              ? ` · ${payment.medioPagoDescripcion}`
                              : ''}
                          </p>
                        </div>
                        <div className="flex-shrink-0 text-right">
                          <p className="text-[15px] font-bold tabular-nums leading-snug text-slate-900 dark:text-white">
                            {money(payment.monto)}
                          </p>
                          <span
                            className={`panel-chip mt-1 ${getStatusBadgeClass(payment.estadoPagoDescripcion)}`}
                          >
                            {payment.estadoPagoDescripcion}
                          </span>
                        </div>
                      </div>

                      <div className="mt-2 flex flex-wrap items-center gap-1.5">
                        <span className="rounded-md bg-slate-50 px-1.5 py-0.5 text-[11px] font-medium text-slate-600 dark:bg-gray-900/50 dark:text-slate-300">
                          {payment.fechaPago
                            ? new Date(payment.fechaPago).toLocaleDateString('es-AR')
                            : t('pages.billing.pending')}
                        </span>
                        {contactChip(payment)}
                      </div>

                      {(showActions && canOperateBilling && !payment.fechaPago) || payment.planId || showActions ? (
                        <div className="mt-2.5 flex flex-wrap items-center justify-end gap-1 border-t border-slate-100 pt-2 dark:border-gray-700/80">
                          {payment.planId ? (
                            <button
                              type="button"
                              onClick={() => openPlan(payment.planId)}
                              className="mr-auto inline-flex h-8 items-center rounded-full border border-slate-200 bg-white px-2.5 text-[11px] font-medium text-slate-600 shadow-sm transition hover:bg-slate-50 dark:border-gray-600 dark:bg-gray-800 dark:text-slate-300 dark:hover:bg-gray-700"
                            >
                              {t('pages.billing.plan')}
                            </button>
                          ) : null}
                          {showActions ? (
                            <button
                              type="button"
                              onClick={() => openInvoice(payment)}
                              className="inline-flex h-8 items-center gap-1 rounded-full border border-slate-200 bg-white px-2.5 text-[11px] font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 dark:border-gray-600 dark:bg-gray-800 dark:text-slate-200 dark:hover:bg-gray-700"
                            >
                              {t('pages.clinicInvoices.generate')}
                            </button>
                          ) : null}
                          {showActions && canOperateBilling && !payment.fechaPago ? (
                            <>
                              <button
                                type="button"
                                onClick={() => openRegisterPayment(payment)}
                                className="inline-flex h-8 items-center gap-1 rounded-full border border-slate-200 bg-white px-2.5 text-[11px] font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 dark:border-gray-600 dark:bg-gray-800 dark:text-slate-200 dark:hover:bg-gray-700"
                                title={t('pages.tenants.registerPayment')}
                              >
                                <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75} aria-hidden>
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m6-6H6" />
                                </svg>
                                {t('pages.tenants.registerPayment')}
                              </button>
                              <button
                                type="button"
                                onClick={() => openContact(payment)}
                                className="inline-flex h-8 items-center gap-1 rounded-full border border-teal-200 bg-teal-50 px-2.5 text-[11px] font-medium text-teal-800 shadow-sm transition hover:bg-teal-100 dark:border-teal-800 dark:bg-teal-950/40 dark:text-teal-200 dark:hover:bg-teal-900/50"
                                title={t('pages.collections.contact.title')}
                              >
                                <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75} aria-hidden>
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                </svg>
                                {t('pages.collections.contact.action')}
                              </button>
                            </>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Tabla md+ */}
          <div className="hidden max-h-[55vh] overflow-x-auto md:block">
            <table className="min-w-[720px] w-full divide-y divide-gray-200 text-sm dark:divide-gray-700">
              <thead className="sticky top-0 z-10 bg-slate-50 dark:bg-gray-900">
                <tr>
                  <th className={`${thClass} text-left`} onClick={() => handleSort('nombreClinica')}>
                    {t('pages.billing.clinic')}
                    {sortIcon('nombreClinica')}
                  </th>
                  <th className={`${thClass} text-right`} onClick={() => handleSort('monto')}>
                    {t('pages.billing.amount')}
                    {sortIcon('monto')}
                  </th>
                  <th
                    className={`${thClass} text-left`}
                    onClick={() => handleSort('estadoPagoDescripcion')}
                  >
                    {t('pages.billing.status')}
                    {sortIcon('estadoPagoDescripcion')}
                  </th>
                  <th className={`${thClass} text-left`} onClick={() => handleSort('fechaPago')}>
                    {t('pages.billing.paymentDate')}
                    {sortIcon('fechaPago')}
                  </th>
                  <th
                    className={`${thClass} text-left`}
                    onClick={() => handleSort('medioPagoDescripcion')}
                  >
                    {t('pages.billing.paymentMethod')}
                    {sortIcon('medioPagoDescripcion')}
                  </th>
                  <th className={`${thClass} text-left`} onClick={() => handleSort('planId')}>
                    {t('pages.billing.plan')}
                    {sortIcon('planId')}
                  </th>
                  {showActions ? (
                    <th className="bg-slate-50 px-3 py-2 text-right text-xs font-medium uppercase text-gray-500 dark:bg-gray-900">
                      {t('common.actions')}
                    </th>
                  ) : null}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {filtered.map((payment) => (
                  <tr key={payment.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-3 py-2 font-medium text-gray-900 dark:text-white">
                      <div className="flex flex-col gap-1">
                        <span>{payment.nombreClinica}</span>
                        {contactChip(payment)}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right font-semibold tabular-nums">
                      {money(payment.monto)}
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={`inline-flex rounded-full px-2 text-xs font-semibold leading-5 ${getStatusBadgeClass(payment.estadoPagoDescripcion)}`}
                      >
                        {payment.estadoPagoDescripcion}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-gray-500 dark:text-gray-400">
                      {payment.fechaPago
                        ? new Date(payment.fechaPago).toLocaleDateString('es-AR')
                        : '—'}
                    </td>
                    <td className="px-3 py-2 text-gray-500 dark:text-gray-400">
                      {payment.medioPagoDescripcion || '—'}
                    </td>
                    <td className="px-3 py-2">
                      {payment.planId ? (
                        <button
                          type="button"
                          onClick={() => openPlan(payment.planId)}
                          className="text-left font-medium text-teal-700 hover:text-teal-800 hover:underline dark:text-teal-300 dark:hover:text-teal-200"
                          title={t('pages.billing.planDetail')}
                        >
                          {planLabel(payment.planId)}
                        </button>
                      ) : (
                        <span className="text-gray-500 dark:text-gray-400">—</span>
                      )}
                    </td>
                    {showActions ? (
                      <td className="whitespace-nowrap px-3 py-2 text-right">
                        <div className="inline-flex flex-wrap items-center justify-end gap-1">
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => openInvoice(payment)}
                          >
                            {t('pages.clinicInvoices.generate')}
                          </Button>
                          {canOperateBilling && !payment.fechaPago ? (
                            <>
                              <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => openRegisterPayment(payment)}
                                title={t('pages.tenants.registerPayment')}
                                aria-label={t('pages.tenants.registerPayment')}
                              >
                                <span className="inline-flex items-center gap-1.5">
                                  <svg
                                    className="h-4 w-4"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    strokeWidth={1.75}
                                    aria-hidden
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      d="M12 6v12m6-6H6"
                                    />
                                  </svg>
                                  {t('pages.tenants.registerPayment')}
                                </span>
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openContact(payment)}
                                title={t('pages.collections.contact.title')}
                                aria-label={t('pages.collections.contact.title')}
                              >
                                <span className="inline-flex items-center gap-1.5">
                                  <svg
                                    className="h-4 w-4"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    strokeWidth={1.75}
                                    aria-hidden
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                                    />
                                  </svg>
                                  {t('pages.collections.contact.action')}
                                </span>
                              </Button>
                            </>
                          ) : null}
                        </div>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <ViewPlanDetailModal
        isOpen={!!selectedPlan}
        plan={selectedPlan}
        onClose={() => setSelectedPlan(null)}
      />

      {contactTarget && (
        <ContactDebtorModal
          isOpen={!!contactTarget}
          debtor={contactTarget}
          onClose={() => setContactTarget(null)}
          onContactSaved={() => {
            void loadPayments();
            if (contactTarget?.tenantId) {
              void fetchLatestContacts([contactTarget.tenantId]).then((map) =>
                setLastContacts((prev) => ({ ...prev, ...map }))
              );
            }
          }}
        />
      )}

      {paymentTenant && (
        <RegisterPaymentModal
          key={String(paymentTenant.id)}
          isOpen={!!paymentTenant}
          tenant={paymentTenant}
          onClose={() => setPaymentTenant(null)}
          onSuccess={() => {
            setPaymentTenant(null);
            void loadPayments();
          }}
        />
      )}

      {invoiceTarget ? (
        <ClinicInvoiceModal
          tenantId={invoiceTarget.tenantId}
          period={periodo}
          clinicName={invoiceTarget.nombre}
          onClose={() => setInvoiceTarget(null)}
        />
      ) : null}
    </div>
  );
};
